import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { EventTrackLabel } from '../constants/ga4';
import { useCartContext } from '../hooks/contexts/cart.context';
import { useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useLoaderScreenContext } from '../hooks/contexts/loader-screen.context';
import { useUserContext } from '../hooks/contexts/user.context';
import useGuardian from '../hooks/useGuardian';
import * as Styles from '../styles/pages/payment';
import GoogleAnalytics from '../trackings/GoogleAnalytics';
import { Checkout } from '../types/checkout';
import { logger } from '../utils/logger';

const PaymentPage: React.FC = () => {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const { couponApplied } = useEventContext();

  const { render, navigateToNextRoute } = useGuardian();
  const {
    namingData,
    prepareCheckoutData,
    requestCheckoutPaymentUrl,
    summaryData,
    updateTransactionId,
    transactionId,
    combosInNaming,
    prepareCheckoutComboData,
  } = useCartContext();
  const { user } = useUserContext();
  const { event } = useEventContext();
  const { toggleLoader } = useLoaderScreenContext();
  const location = useLocation();
  const { currentLocale : { locale } } = useI18nContext();

  async function requestCheckoutUrl({
    checkoutData,
  }: {
    checkoutData: Checkout;
  }) {
    toggleLoader({ show : true });
    const gatewayDate = await requestCheckoutPaymentUrl(checkoutData);
    updateTransactionId(String(gatewayDate?.transactionId));
    if (!gatewayDate) {
      toggleLoader({ show : false });
      return;
    }

    if (gatewayDate.checkoutUrl === 'approved') {
      toggleLoader({ show : false });
      navigateToNextRoute({ route : 'payment-confirmed' });
      return;
    }

    setIframeUrl(gatewayDate.checkoutUrl);
    logger.info(gatewayDate.checkoutUrl);
    toggleLoader({ show : false });
  }

  const handleAnalyticsTracking = (paymentMethod?: string) => {
    GoogleAnalytics.trackEvent(
      EventTrackLabel.BEGIN_CHECKOUT,
      {
        currency : String(event?.accepted_currencies),
        summaryData,
        event,
        couponApplied,
        value : summaryData.reduce(
          (acc, ticket) => acc + ticket.accumulatedValueInCart,
          0
        ),
        items : summaryData.map((ticket) => ({
          item_id : String(event?.id),
          item_name : String(event?.title),
          coupon : couponApplied ? couponApplied.coupon.name : '',
          item_category : ticket.date,
          item_brand : ticket.group,
          item_variant : ticket.title,
          quantity : ticket.quantity,
          discount : couponApplied ? couponApplied.coupon.value_discount : 0,
          price : ticket.price,
        }))
      },
    );

    if (paymentMethod)
      GoogleAnalytics.trackEvent(        EventTrackLabel.ADD_PAYMENT_INFO,
        {
          currency : String(event?.accepted_currencies),
          payment_type : paymentMethod,
          summaryData,
          event,
          couponApplied,
          value : summaryData.reduce(
            (acc, ticket) => acc + ticket.accumulatedValueInCart,
            0
          ),
          items : summaryData.map((ticket) => ({
            item_id : String(event?.id),
            item_name : String(event?.title),
            coupon : couponApplied ? couponApplied.coupon.name : '',
            item_category : ticket.date,
            item_brand : ticket.group,
            item_variant : ticket.title,
            quantity : ticket.quantity,
            discount : couponApplied ? couponApplied.coupon.value_discount : 0,
            price : ticket.price,
          }))
        });
  };

  useEffect(() => {
    if (!user || !event) return;
    const data = prepareCheckoutData({ namingData, user, event });
    const comboData =
      prepareCheckoutComboData({
        comboNamingData : combosInNaming,
        user,
        event,
      }) ?? [];
    if (!data) return;

    let definitiveData = data;
    if (comboData) {
      definitiveData = {
        ...definitiveData,
        tickets : [...data.tickets, ...comboData.tickets],
      };
    }

    requestCheckoutUrl({ checkoutData : definitiveData });
  }, [user, event, namingData, combosInNaming]);

  useEffect(() => {
    if (!iframeUrl) return;

    const checkout = window.IngresseCheckout;

    checkout?.mount('checkout-frame', iframeUrl, {
      locale : locale,
      onSuccess : (eventDetails) => {
        handleSuccess(eventDetails);
      },
      onTimeout : () => {
        // handleTimeout();
      },
      style : {
        textColorPrimary : '212B36',
        textColorSecondary : '454F5B',
        textColorHighlight : '2280EF',
        textColorAccent : 'FF7400',

        bgColorPrimary : 'F4F6F8',
        bgColorSecondary : 'F9FAFB',
        bgColorAccent : 'FF740014',

        borderColorPrimary : 'C4CDD5',
        borderColorAccent : 'FF9800',

        buttonColorPrimary : 'FF7400',
        buttonColorHover : 'FF9800',
      },
    });
  }, [iframeUrl]);

  useEffect(() => {
    return () => {
      if (window.IngresseCheckout) {
        window.IngresseCheckout.cancel();
      }
    };
  }, []);

  const handleSuccess = ({ status, type }: IPaymentDetails) => {
    handleAnalyticsTracking(type);

    if (status === 'paid' || status === 'success') {
      navigateToNextRoute({ route : 'payment-confirmed' });
      return;
    }
    if (status === 'partially paid') {
      navigateToNextRoute({ route : 'partially-paid' });
      return;
    }
    if (status === 'pending') {
      navigateToNextRoute({ route : 'payment-in-process' });
      return;
    }

    GoogleAnalytics.trackTransactionCancelled(EventTrackLabel.CANCELED_TRANSACTION, {
      event_category : `Embedstore - Etapa ${location.pathname}`,
      event_label : `Usuário: ${String(user?.id)}. Transação ${transactionId}`,
    });
  };

  if (!iframeUrl) return null;

  return render(
    <Styles.CheckoutContainer id="checkout-frame"></Styles.CheckoutContainer>
  );
};

export default PaymentPage;
